import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Check, Wine, Receipt, Gift, Copy, X } from "lucide-react";
import { api, fmtDate, fmtDateTime, money } from "../lib/api";
import { CheckinHistory, bundleTitle } from "../components/CheckinHistory";
import { Screen, PageHeader, Empty } from "../components/ui-kit";
import { toast } from "sonner";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - new Date()) / 864e5));

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3"><span className="text-[#A19DB0]">{label}</span><span className="text-[#E8E4F0] font-medium text-right truncate">{value}</span></div>
);

function PassCard({ p }) {
  const [open, setOpen] = useState(false);
  const hasIncl = (p.inclusions || []).length > 0;
  let title = p.package_name;
  if (p.group === "classes") title = `${p.credits_left}/${p.credits_total} · ${p.class_level ? p.class_level[0].toUpperCase() + p.class_level.slice(1) : "Class"} Class Pass`;
  else if (p.bundle_size > 1) title = bundleTitle(p);
  else if (p.group === "socials") title = p.credits_total > 1 ? `${p.package_name} · ${p.credits_left}/${p.credits_total} entries` : `${p.package_name} · ${p.credits_left} entr${p.credits_left !== 1 ? "ies" : "y"}`;
  return (
    <div data-testid={`wallet-pass-${p.id}`} className="rounded-2xl border border-white/10 bg-white/5 p-4 relative overflow-hidden">
      <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: p.color || "#9D4EDD" }} />
      <div className="flex items-start justify-between gap-2 pl-2">
        <div className="min-w-0">
          <p className="font-medium">{title}{p.option ? ` · ${p.option}` : ""}</p>
          {(p.social_date || (p.event_dates || []).length > 0) && <p data-testid={`wallet-pass-date-${p.id}`} className="text-[#D4AF37] text-xs mt-0.5">For {p.social_date ? `${p.social_title ? `${p.social_title} · ` : ""}${fmtDate(p.social_date)}` : p.event_dates.map((e) => `${e.title} · ${fmtDate(e.starts_at)}`).join(", ")}</p>}
          <p className="text-[#A19DB0] text-xs mt-0.5">Expires in {daysLeft(p.expires_at)}d · {fmtDate(p.expires_at)}</p>
          {p.package_id === "beginner-bonus-social" && (
            <span data-testid={`wallet-bonus-tag-${p.id}`} className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] font-semibold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-2.5 py-1">
              <Gift size={13} /> Free social — valid 30 days
            </span>
          )}
          {p.shared_by && <p className="text-[#D4AF37] text-xs mt-0.5">Shared by {p.shared_by}</p>}
          {p.drink_total > 0 && <p className="text-[#D4AF37] text-xs mt-1 flex items-center gap-1"><Wine size={13} /> {p.drink_left}/{p.drink_total} drink coupons</p>}
        </div>
        <span className="text-[10px] uppercase tracking-wide text-[#A19DB0] bg-white/5 rounded-full px-2 py-1 shrink-0">{p.group}</span>
      </div>
      <CheckinHistory p={p} />
      {hasIncl && (
        <>
          <button onClick={() => setOpen((o) => !o)} data-testid={`wallet-pass-expand-${p.id}`} className="flex items-center gap-1 text-[#D4AF37] text-sm mt-2 pl-2">What you get <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
          {open && <ul className="mt-2 space-y-1.5 pl-2">{p.inclusions.map((it, i) => <li key={`${p.id}-incl-${i}`} className="flex gap-2 text-sm text-[#E8E4F0]"><Check size={15} className="text-[#D4AF37] mt-0.5 shrink-0" /> {it}</li>)}</ul>}
        </>
      )}
    </div>
  );
}

const UsedPassRow = ({ p }) => (
  <div data-testid={`wallet-used-pass-${p.id}`} className="flex items-center justify-between gap-3 rounded-2xl bg-white/[0.03] border border-white/10 p-4 opacity-80">
    <div className="min-w-0">
      <p className="font-medium truncate">{p.package_name}{p.bundle_size > 1 ? ` · Pass ${p.bundle_index} of ${p.bundle_size}` : ""}</p>
      <p className="text-[#A19DB0] text-xs mt-0.5" data-testid={`wallet-used-at-${p.id}`}>Scanned {fmtDateTime(p.used_at || p.checked_in_at)}</p>
    </div>
    <span className="text-[10px] uppercase tracking-wide font-semibold text-[#A19DB0] bg-white/10 rounded-full px-2.5 py-1 shrink-0">Used</span>
  </div>
);

export default function Wallet() {
  const [passes, setPasses] = useState([]);
  const [used, setUsed] = useState([]);
  const [payments, setPayments] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [ref, setRef] = useState(null);
  const [sel, setSel] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/passes").then((r) => setPasses(r.data)).catch(() => {});
    api.get("/passes/history").then((r) => setUsed(r.data)).catch(() => {});
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/referral").then((r) => setRef(r.data)).catch(() => {});
  }, []);

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  return (
    <Screen>
      <PageHeader overline="Your account" title="Wallet" back />

      {wallet && (
        <div data-testid="wallet-credits" className="rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-5 mb-6">
          <p className="text-xs text-[#A19DB0]">Class passes</p>
          <p className="font-display text-4xl font-bold text-[#D4AF37] mt-1" data-testid="credit-balance">{wallet.available}</p>
          <p className="text-[#A19DB0] text-xs mt-1">{wallet.nearest_expiry ? `Next passes expire ${fmtDate(wallet.nearest_expiry)}` : "Buy a class pass in Packages to book classes"}</p>
          {(wallet.batches || []).length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="wallet-class-passes">
              {wallet.batches.map((b, i) => (
                <div key={b.id || i} className="flex items-center justify-between text-sm bg-white/5 rounded-xl px-3 py-2">
                  <span className="text-[#E8E4F0] truncate">{b.package_name || "Class pass"}</span>
                  <span className="text-[#A19DB0] text-xs shrink-0 ml-2">{b.credits_remaining}/{b.credits_total} left · exp {fmtDate(b.expires_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {ref && (
        <div data-testid="referral-card" className="rounded-2xl border border-[#9D4EDD]/30 bg-[#9D4EDD]/[0.08] p-5 mb-6">
          <p className="flex items-center gap-2 font-display text-lg font-semibold"><Gift size={17} className="text-[#D4AF37]" /> Refer a friend</p>
          <p className="text-[#A19DB0] text-sm mt-1">When a friend joins with your code and makes their first purchase, you get a free <span className="text-[#D4AF37]">Social pass</span> (60-day expiry).</p>
          <div className="flex items-center gap-2 mt-3">
            <div className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 font-display text-xl tracking-widest text-[#D4AF37]" data-testid="referral-code">{ref.code}</div>
            <button onClick={() => copy(ref.code)} data-testid="copy-referral-code" className="rounded-xl bg-white/10 border border-white/10 px-3 py-2.5 text-sm flex items-center gap-1.5"><Copy size={14} /> Code</button>
            <button onClick={() => copy(ref.link)} data-testid="copy-referral-link" className="rounded-xl bg-[#D4AF37] text-black font-semibold px-3 py-2.5 text-sm flex items-center gap-1.5"><Copy size={14} /> Link</button>
          </div>
          <p className="text-[#A19DB0] text-xs mt-2">{ref.referred} referred · {ref.rewarded} reward{ref.rewarded !== 1 ? "s" : ""} earned</p>
        </div>
      )}

      <button onClick={() => navigate("/packages")} data-testid="wallet-buy-passes" className="w-full mb-6 rounded-full py-3.5 font-semibold bg-[#D4AF37] text-black hover:bg-[#E5C158] active:scale-[0.98] transition-all">Buy passes here</button>

      <h2 className="font-display text-lg font-semibold mb-3">Active passes</h2>
      {passes.length === 0 ? (
        <Empty text="No active passes. Buy one from Packages." />
      ) : (
        <div className="space-y-3" data-testid="wallet-passes">{passes.map((p) => <PassCard key={p.id} p={p} />)}</div>
      )}

      {used.length > 0 && (
        <>
          <h2 className="font-display text-lg font-semibold mt-8 mb-3">Scanned passes</h2>
          <div className="space-y-2" data-testid="wallet-used-passes">{used.map((p) => <UsedPassRow key={p.id} p={p} />)}</div>
        </>
      )}

      <h2 className="font-display text-lg font-semibold mt-8 mb-3">Purchase history</h2>
      {payments.length === 0 ? (
        <Empty icon={Receipt} text="No purchases yet." />
      ) : (
        <div className="space-y-2" data-testid="wallet-history">
          {payments.map((pm) => (
            <button key={pm.id} onClick={() => setSel(pm)} data-testid={`payment-${pm.id}`} className="w-full flex items-center justify-between rounded-2xl bg-white/5 border border-white/10 p-4 text-left hover:bg-white/[0.08] active:scale-[0.99] transition-all">
              <div className="min-w-0"><p className="font-medium truncate">{pm.package_name}</p><p className="text-[#A19DB0] text-xs">{fmtDateTime(pm.created_at)}{pm.discount_percent ? ` · −${pm.discount_percent}%` : ""}</p></div>
              <span className="flex items-center gap-2 shrink-0"><span className="font-display text-lg font-bold text-[#D4AF37]">{pm.amount === 0 ? "Free" : money(pm.amount)}</span><ChevronRight size={18} className="text-[#A19DB0]" /></span>
            </button>
          ))}
        </div>
      )}

      {sel && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setSel(null)} data-testid="payment-detail-modal">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-display text-xl font-semibold flex items-center gap-2"><Receipt size={19} className="text-[#D4AF37]" /> Purchase details</p>
              <button onClick={() => setSel(null)} data-testid="payment-detail-close" className="text-[#A19DB0]"><X size={18} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <Row label="Ticket / package" value={sel.package_name} />
              {sel.option && <Row label="Option" value={sel.option} />}
              <Row label="Date" value={fmtDateTime(sel.created_at)} />
              <Row label="Amount" value={sel.amount === 0 ? "Free" : money(sel.amount)} />
              {sel.discount_percent ? <Row label="Discount" value={`−${sel.discount_percent}%`} /> : null}
              {sel.method && <Row label="Method" value={sel.method} />}
              <div>
                <p className="text-[#A19DB0] text-xs">Reference code</p>
                <div className="flex items-center gap-2 mt-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                  <span data-testid="payment-detail-code" className="flex-1 font-mono text-xs truncate text-[#E8E4F0]">{sel.id}</span>
                  <button onClick={() => copy(sel.id)} data-testid="payment-detail-copy" className="text-[#A19DB0] hover:text-white shrink-0"><Copy size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
}
